import { FormEditTabType, FormSettingsTabType } from '@doss/common-all'
import { IconType } from 'react-icons'
import { FiChevronDown } from 'react-icons/fi'
import {
  PiBookOpen,
  PiCalendarBlank,
  PiCheckSquareFill,
  PiClock,
  PiEnvelopeSimple,
  PiFile,
  PiHash,
  PiListBullets,
  PiSignature,
  PiTextAa,
  PiTextAlignLeft,
} from 'react-icons/pi'
import { FormFieldType } from '~/common/generated/apollo-operations'

export const DEFAULT_TAB = FormEditTabType.FORM_CONTENT
export const DEFAULT_SETTINGS_TAB = FormSettingsTabType.DETAILS

export enum RightPanelOperation {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
}

export enum RightPanelObjectType {
  FORM_FIELD = 'formField',
  FORM_CATEGORY = 'formCategory',
  FORM_PAGE = 'formPage',
  FORM = 'form',
  WORKSPACE_BUTTON = 'workspaceButton',
}

export enum LayoutTypes {
  PAGE = 'Page',
  CATEGORY = 'Category',
}

export const LayoutItems = [
  { icon: PiBookOpen, label: LayoutTypes.PAGE },
  { icon: PiListBullets, label: LayoutTypes.CATEGORY },
]

export enum InputTypes {
  NUMBER = 'Number',
  SHORT_TEXT = 'Short Text',
  LONG_TEXT = 'Long Text',
  DATE = 'Date',
  DATE_TIME = 'Datetime',
  DROPDOWN = 'Dropdown',
  CHECKBOX = 'Checkbox',
  RADIO = 'Radio',
  EMAIL = 'Email',
  FILE_UPLOAD = 'File Upload',
  SIGNATURE = 'Signature',
}

export type InputItem = {
  icon: IconType
  label: InputTypes
}

const inputItemsMap = (type: InputTypes): IconType => {
  switch (type) {
    case InputTypes.NUMBER:
      return PiHash
    case InputTypes.SHORT_TEXT:
      return PiTextAa
    case InputTypes.LONG_TEXT:
      return PiTextAlignLeft
    case InputTypes.DATE:
      return PiCalendarBlank
    case InputTypes.DATE_TIME:
      return PiClock
    case InputTypes.DROPDOWN:
      return FiChevronDown
    case InputTypes.CHECKBOX:
      return PiCheckSquareFill
    case InputTypes.RADIO:
      return FiChevronDown
    case InputTypes.EMAIL:
      return PiEnvelopeSimple
    case InputTypes.FILE_UPLOAD:
      return PiFile
    case InputTypes.SIGNATURE:
      return PiSignature
  }
}

// Removing RADIO for now because it hasn't been properly QA'd yet
const visibleInputs = Object.values(InputTypes).filter((key) => key !== InputTypes.RADIO)
export const InputItems: InputItem[] = visibleInputs.map((input) => ({
  icon: inputItemsMap(input),
  label: input,
}))

export const InputItemsMap: Map<InputTypes, InputItem> = new Map(
  InputItems.map((item) => [item.label, { icon: item.icon, label: item.label }]),
)

export const FormFieldTypeToInputType: Record<FormFieldType, InputTypes> = {
  [FormFieldType.NUMBER]: InputTypes.NUMBER,
  [FormFieldType.SHORT_TEXT]: InputTypes.SHORT_TEXT,
  [FormFieldType.LONG_TEXT]: InputTypes.LONG_TEXT,
  [FormFieldType.DATE]: InputTypes.DATE,
  [FormFieldType.DATE_TIME]: InputTypes.DATE_TIME,
  [FormFieldType.DROPDOWN]: InputTypes.DROPDOWN,
  [FormFieldType.BOOLEAN]: InputTypes.CHECKBOX,
  [FormFieldType.CHECKBOXES]: InputTypes.CHECKBOX,
  [FormFieldType.RADIO_BUTTON]: InputTypes.RADIO,
  [FormFieldType.EMAIL]: InputTypes.EMAIL,
  [FormFieldType.FILE]: InputTypes.FILE_UPLOAD,
  [FormFieldType.LOCATION]: InputTypes.LONG_TEXT,
  [FormFieldType.SIGNATURE]: InputTypes.SIGNATURE,
}

export const InputTypeToFormFieldType: Record<InputTypes, FormFieldType> = {
  [InputTypes.NUMBER]: FormFieldType.NUMBER,
  [InputTypes.SHORT_TEXT]: FormFieldType.SHORT_TEXT,
  [InputTypes.LONG_TEXT]: FormFieldType.LONG_TEXT,
  [InputTypes.DATE]: FormFieldType.DATE,
  [InputTypes.DATE_TIME]: FormFieldType.DATE_TIME,
  [InputTypes.DROPDOWN]: FormFieldType.DROPDOWN,
  [InputTypes.CHECKBOX]: FormFieldType.BOOLEAN,
  [InputTypes.RADIO]: FormFieldType.RADIO_BUTTON,
  [InputTypes.EMAIL]: FormFieldType.EMAIL,
  [InputTypes.FILE_UPLOAD]: FormFieldType.FILE,
  [InputTypes.SIGNATURE]: FormFieldType.SIGNATURE,
}

export enum DropdownOptionCategories {
  LAYOUT = 'layout',
  INPUT = 'input',
}

export const InputDropdownOptions = InputItems.map((item) => {
  return {
    label: item.label,
    value: InputTypeToFormFieldType[item.label],
    icon: item.icon,
    type: DropdownOptionCategories.INPUT,
    disabled: false,
  }
})

export const LayoutDropdownOptions = LayoutItems.map((item) => {
  return {
    label: item.label,
    value: item.label,
    icon: item.icon,
    type: DropdownOptionCategories.LAYOUT,
    disabled: false,
  }
})

export type InputDropdownOptionType = (typeof InputDropdownOptions)[number]

export const CreateDropdownOptions = [...LayoutDropdownOptions, ...InputDropdownOptions]

export type CreateDropdownOptionType = (typeof CreateDropdownOptions)[number]

export function isInputType(type: unknown): type is InputTypes {
  return Object.values(InputTypes).includes(type as InputTypes)
}

export function isLayoutType(type: unknown): type is LayoutTypes {
  return Object.values(LayoutTypes).includes(type as LayoutTypes)
}

export function isFormFieldType(type: unknown): type is FormFieldType {
  return Object.values(FormFieldType).includes(type as FormFieldType)
}

export function getIconForInputType(type: InputTypes): IconType {
  return InputItemsMap.get(type)?.icon ?? FiChevronDown
}

export function getIconForFieldType(type: FormFieldType): IconType {
  return InputItemsMap.get(FormFieldTypeToInputType[type])?.icon ?? FiChevronDown
}
